import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { Demarche, SousDemarche, ApiResponse, PaginatedResponse } from '../../types';

interface DemarcheWithExtras extends Demarche {
  client_nom?: string;
  client_prenom?: string;
  type_demarche_nom?: string;
}

interface DemarchesState {
  demarches: Demarche[];
  currentDemarche: DemarcheWithExtras | null;
  sousDemarches: SousDemarche[];
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  } | null;
}

const initialState: DemarchesState = {
  demarches: [],
  currentDemarche: null,
  sousDemarches: [],
  loading: false,
  error: null,
  pagination: null,
};

// Interface spécifique pour la réponse des démarches
interface DemarchesResponse {
  demarches: Demarche[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Actions asynchrones
export const fetchDemarches = createAsyncThunk(
  'demarches/fetchAll',
  async (params: { page?: number; limit?: number; search?: string; statut?: string; client_id?: number }) => {
    const response = await api.get<ApiResponse<DemarchesResponse>>('/demarches', { params });
    return response.data.data!;
  }
);

export const fetchDemarcheById = createAsyncThunk(
  'demarches/fetchById',
  async (id: number) => {
    const response = await api.get<ApiResponse<{ demarche: DemarcheWithExtras; sous_demarches: SousDemarche[] }>>(`/demarches/${id}`);
    return response.data.data!;
  }
);

export const createDemarche = createAsyncThunk(
  'demarches/create',
  async (demarcheData: Partial<Demarche>) => {
    const response = await api.post<ApiResponse<{ id: number }>>('/demarches', demarcheData);
    return response.data;
  }
);

export const updateDemarche = createAsyncThunk(
  'demarches/update',
  async ({ id, data }: { id: number; data: Partial<Demarche> }) => {
    const response = await api.put<ApiResponse<null>>(`/demarches/${id}`, data);
    return { id, data };
  }
);

export const deleteDemarche = createAsyncThunk(
  'demarches/delete',
  async (id: number) => {
    await api.delete(`/demarches/${id}`);
    return id;
  }
);

export const createSousDemarche = createAsyncThunk(
  'demarches/createSousDemarche',
  async ({ demarcheId, data }: { demarcheId: number; data: Partial<SousDemarche> }) => {
    const response = await api.post<ApiResponse<{ id: number }>>(`/demarches/${demarcheId}/sous-demarches`, data);
    return response.data;
  }
);

export const updateSousDemarche = createAsyncThunk(
  'demarches/updateSousDemarche',
  async ({ id, data }: { id: number; data: Partial<SousDemarche> }) => {
    await api.put(`/demarches/sous-demarches/${id}`, data);
    return { id, data };
  }
);

export const deleteSousDemarche = createAsyncThunk(
  'demarches/deleteSousDemarche',
  async (id: number) => {
    await api.delete(`/demarches/sous-demarches/${id}`);
    return id;
  }
);

export const fetchDossiersARevoir = createAsyncThunk(
  'demarches/dossiersARevoir',
  async () => {
    const response = await api.get<ApiResponse<Demarche[]>>('/demarches/dossiers-a-revoir');
    return response.data.data!;
  }
);

export const fetchInterventionsRequises = createAsyncThunk(
  'demarches/interventionsRequises',
  async () => {
    const response = await api.get<ApiResponse<Demarche[]>>('/demarches/interventions-requises');
    return response.data.data!;
  }
);

// Slice
const demarchesSlice = createSlice({
  name: 'demarches',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentDemarche: (state) => {
      state.currentDemarche = null;
      state.sousDemarches = [];
    },
  },
  extraReducers: (builder) => {
    // Fetch all demarches
    builder.addCase(fetchDemarches.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchDemarches.fulfilled, (state, action) => {
      state.loading = false;
      state.demarches = action.payload.demarches;
      state.pagination = action.payload.pagination;
    });
    builder.addCase(fetchDemarches.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Erreur lors du chargement des démarches';
    });

    // Fetch demarche by ID
    builder.addCase(fetchDemarcheById.fulfilled, (state, action) => {
      state.currentDemarche = action.payload.demarche;
      state.sousDemarches = action.payload.sous_demarches;
    });

    // Delete demarche
    builder.addCase(deleteDemarche.fulfilled, (state, action) => {
      state.demarches = state.demarches.filter(d => d.id !== action.payload);
    });

    // Delete sous-demarche
    builder.addCase(deleteSousDemarche.fulfilled, (state, action) => {
      state.sousDemarches = state.sousDemarches.filter(sd => sd.id !== action.payload);
    });
  },
});

export const { clearError, clearCurrentDemarche } = demarchesSlice.actions;
export default demarchesSlice.reducer;
